import React, { useState, useEffect } from 'react';
import { Trash2, Moon, Sun, Bell, ShieldAlert, Loader2, Save } from 'lucide-react';
import Page from './Page';
import { utils } from './utils/index';

const REACT_APP_API_HOST = utils.config.REACT_APP_API_HOST;

/**
 * SettingsPage component provides system configuration options including theme toggle,
 * notification preferences, storage constraints, and a critical data purging mechanism.
 *
 * @component
 * @returns {React.JSX.Element} The rendered settings page component.
 */
export default function SettingsPage() {
  // Theme state initialized from localStorage
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      return localStorage.getItem('theme') === 'dark';
    } catch (e) {
      console.error('Failed to read theme from localStorage:', e);
      return false;
    }
  });

  // Notification and system configuration states
  const [emailFrequency, setEmailFrequency] = useState('monthly');
  const [storageLimit, setStorageLimit] = useState(50);
  
  // Action status monitoring states
  const [loading, setLoading] = useState(false);
  const [actionStatus, setActionStatus] = useState('idle'); // 'idle' | 'saving' | 'success' | 'error'
  const [statusMessage, setStatusMessage] = useState('');

  // Danger zone confirmation modal inputs
  const [deleteConfirmationInput, setDeleteConfirmationInput] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  /**
   * Automatically displays and dispatches temporary UI notifications.
   *
   * @param {Object} params - The parameter object.
   * @param {string} params.text - The localized text message to display.
   * @param {string} params.status - The operational status type ('success' | 'error' | 'idle').
   * @returns {void}
   */
  const triggerStatusNotification = ({ text, status }) => {
    // Sanitize message strings before setting to local component state
    const cleanText = String(text);
    setStatusMessage(cleanText);
    setActionStatus(status);

    setTimeout(() => {
      setStatusMessage('');
      setActionStatus('idle');
    }, 6000);
  };

  // Sync theme modifications across the layout document body and localStorage
  useEffect(() => {
    try {
      if (isDarkMode) {
        document.documentElement.classList.add('dark');
        localStorage.setItem('theme', 'dark');
      } else {
        document.documentElement.classList.remove('dark');
        localStorage.setItem('theme', 'light');
      }
      console.debug('System theme updated. Is dark mode active:', isDarkMode);
    } catch (error) {
      console.error('Failed to sync theme changes with browser storage:', error);
    }
  }, [isDarkMode]);

  /**
   * Handles saving application and notification configuration settings to the backend service.
   *
   * @async
   * @param {Object} params - The parameter object.
   * @param {React.FormEvent} params.event - The underlying form submission element.
   * @returns {Promise<void>} Resolves when the save operation completes.
   * @throws {Error} Logs error traces cleanly if validation or structural transit crashes.
   */
  const handleSaveSettings = async ({ event }) => {
    event.preventDefault();
    if (loading) return;

    console.log('Production log: Initiating settings save update.');
    console.debug('Debug log: Pre-sanitized form values:', { emailFrequency, storageLimit });
    
    setLoading(true);

    try {
      // Input validation and explicit escaping/sanitization
      const sanitizedFrequency = String(emailFrequency).replace(/[^\w-]/g, '');
      const sanitizedLimit = Math.max(1, Math.min(1000, Number(storageLimit)));

      if (isNaN(sanitizedLimit)) {
        throw new Error('Invalid storage limit numeric format.');
      }

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authorization credentials not found in application scope.');
      }

      const response = await fetch(`${REACT_APP_API_HOST}/api/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          emailFrequency: sanitizedFrequency,
          storageLimit: sanitizedLimit
        })
      });

      if (!response.ok) {
        // Strict error mitigation: do not bubble raw responses or trace text to the UI state
        throw new Error(`Server responded with status code ${response.status}`);
      }

      console.log('Production log: General settings saved successfully.');
      triggerStatusNotification({ text: 'ההגדרות נשמרו בהצלחה', status: 'success' });
    } catch (error) {
      console.error('Error occurred while saving settings configuration:', error);
      triggerStatusNotification({ text: 'שגיאה בשמירת ההגדרות. אנא נסה שנית.', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles the programmatic destructive purging of all remote user data.
   *
   * @async
   * @param {Object} [params={}] - The configuration parameters.
   * @returns {Promise<void>} Resolves when the purge tracking completes successfully.
   * @throws {Error} Encapsulates network runtime failures without leaking trace vectors.
   */
  const handlePurgeAllData = async (params = {}) => {
    // Escape verification to confirm explicit structural match of security input phrase
    if (deleteConfirmationInput !== 'DELETE') {
      console.warn('Data purge rejected: Confirmation text validation failed.');
      return;
    }

    console.log('Production log: Destructive action initiated - Purging all receipts data.');
    setLoading(true);
    setShowDeleteModal(false);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Active security context credentials missing.');
      }

      const response = await fetch(`${REACT_APP_API_HOST}/api/receipts/purge-all`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Data purge failed with HTTP standard code ${response.status}`);
      }

      console.log('Production log: Direct database data purge executed successfully.');
      setDeleteConfirmationInput('');
      triggerStatusNotification({ text: 'כל הנתונים נמחקו מהמערכת לצמיתות.', status: 'success' });
    } catch (error) {
      console.error('Critical failure during data purge operation:', error);
      triggerStatusNotification({ text: 'שגיאה חמורה: לא ניתן היה למחוק את הנתונים.', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      {/* Toast Alert Feedback Banner */}
      {statusMessage && (
        <div className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50 p-4 rounded-xl shadow-lg border text-sm font-medium transition-all max-w-md w-auto text-right ${
          actionStatus === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
          actionStatus === 'error' ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-indigo-50 border-indigo-200 text-indigo-800'
        }`}>
          <div className="flex items-center gap-2 justify-end">
            <span>{statusMessage}</span>
            {loading && <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />}
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="border-b border-slate-200 pb-6 mb-6 text-right">
        <h2 className="text-xl font-bold text-slate-900">הגדרות מערכת</h2>
        <p className="text-xs text-slate-500 mt-1">ניהול העדפות תצוגה, התראות ומחיקת מידע מערכת.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 max-w-4xl mx-auto text-right" dir="rtl">
        
        {/* Preference Settings Form */}
        <form onSubmit={(e) => handleSaveSettings({ event: e })} className="space-y-6">
          
          {/* Theme Preference Card */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-4">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 bg-slate-100 rounded-md text-slate-600">
                  {isDarkMode ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                </div>
                <h3 className="font-bold text-slate-800 text-sm">מראה ותצוגה</h3>
              </div>
            </div>
            
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium text-slate-700">מצב כהה (Dark Mode)</p>
                <p className="text-xs text-slate-400 mt-0.5">שינוי צבעי הממשק להתאמה לתנאי תאורה חשוכים.</p>
              </div>
              <button
                type="button"
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                  isDarkMode ? 'bg-indigo-600' : 'bg-slate-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                    isDarkMode ? '-translate-x-6' : '-translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* System Notifications & Controls Card */}
         
        </form> 

        {/* Danger Zone Section */}
        <div className="bg-rose-50/40 border border-rose-200 rounded-2xl shadow-sm p-6 mt-4">
          <div className="flex items-center gap-2.5 border-b border-rose-100 pb-4 mb-4">
            <div className="p-1.5 bg-rose-100 rounded-md text-rose-600">
              <ShieldAlert className="w-4 h-4" />
            </div>
            <h3 className="font-bold text-rose-900 text-sm">אזור מסוכן (Danger Zone)</h3>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-sm font-bold text-rose-900">מחיקת כל הנתונים מהמערכת</p>
              <p className="text-xs text-rose-700/80 mt-1 max-w-xl">
                פעולה זו היא סופית ואינה ניתנת לביטול. כל מסמכי בית העסק, הקבלות, היסטוריית הדיווחים והמידע המאוחסן יימחקו לצמיתות מבסיס הנתונים של השרת.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              className="flex items-center gap-1.5 px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-medium text-xs rounded-lg transition-colors whitespace-nowrap shadow-sm"
            >
              <Trash2 className="w-3.5 h-3.5" />
              מחק את כל הנתונים
            </button>
          </div>
        </div>

      </div>

      {/* Confirmation Modal Component */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full border border-slate-200 shadow-xl overflow-hidden text-right" dir="rtl">
            <div className="p-6">
              <div className="flex items-center gap-3 text-rose-600 mb-3">
                <ShieldAlert className="w-6 h-6" />
                <h4 className="text-lg font-bold">האם אתה בטוח לחלוטין?</h4>
              </div>
              <p className="text-sm text-slate-600 mb-4">
                פעולה זו תמחק את כל הנתונים ללא אפשרות שחזור. כדי לאשר את המחיקה, אנא הקלד <span className="font-mono font-bold text-slate-900 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">DELETE</span> בתיבה למטה:
              </p>
              <input
                type="text"
                value={deleteConfirmationInput}
                onChange={(e) => setDeleteConfirmationInput(e.target.value)}
                placeholder="הקלד DELETE כאן..."
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-500/10 transition-all font-mono text-center"
              />
            </div>
            <div className="bg-slate-50 px-6 py-4 flex items-center justify-end gap-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmationInput('');
                }}
                className="px-4 py-2 bg-white border border-slate-200 text-slate-700 font-medium text-xs rounded-lg hover:bg-slate-50 transition-colors"
              >
                ביטול
              </button>
              <button
                type="button"
                disabled={deleteConfirmationInput !== 'DELETE'}
                onClick={() => handlePurgeAllData()}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:bg-slate-200 text-white disabled:text-slate-400 font-medium text-xs rounded-lg transition-colors shadow-sm"
              >
                אני מבין, מחק הכל
              </button>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}